// Incident detail bottom sheet for mobile
// Displays full incident information with native mobile interaction (swipe-to-dismiss)
// Allows status management for authorized roles (admin, manager)
// Features:
// - Swipe-to-dismiss for natural mobile UX
// - Integrated category badge in metadata block
// - Status updates inline without closing
// - File attachments with preview grid and lightbox
// - Read-only mode for director and operator roles
// UPDATED: Removed UserX icon (vestige hr category)

import { useState, useEffect } from 'react';
import { Users, PackageX, Wrench, HelpCircle, AlertTriangle, User, MapPin, Calendar, CheckCircle, Paperclip, FileText, X as XIcon } from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';
import {
  updateIncidentStatus,
  getCategoryColorClass,
  getStatusColorClass,
  getCategoryInfo,
  getStatusInfo,
  INCIDENT_STATUSES
} from '../../lib/incidents';
import type { IncidentWithDetails, Incident } from '../../lib/incidents';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { getFullName } from '../../lib/supabase';
import { CustomSelect } from '../ui/CustomSelect';
import {
  getIncidentAttachments,
  getSignedUrl,
  isImageFile,
  isPdfFile,
  type IncidentAttachment
} from '../../lib/attachments';

interface IncidentDetailBottomSheetProps {
  incident: IncidentWithDetails;
  onClose: () => void;
}

const ICON_MAP = {
  Users,
  PackageX,
  Wrench,
  HelpCircle
} as const;

export function IncidentDetailBottomSheet({ incident, onClose }: IncidentDetailBottomSheetProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentIncident, setCurrentIncident] = useState(incident);
  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  // Check if current user is director or operator (read-only mode)
  const isReadOnly = user?.role === 'director' || user?.role === 'operator';

  // Load attachments when bottom sheet opens
  useEffect(() => {
    const loadAttachments = async () => {
      setIsLoadingAttachments(true);
      try {
        const data = await getIncidentAttachments(incident.id);
        setAttachments(data);

        // Pre-generate signed URLs for all attachments in parallel
        const urlPromises = data.map(async (attachment) => {
          try {
            const signedUrl = await getSignedUrl(attachment.file_url);
            return { id: attachment.id, url: signedUrl };
          } catch (error) {
            console.error(`Error generating signed URL for ${attachment.file_name}:`, error);
            return { id: attachment.id, url: null };
          }
        });

        const urls = await Promise.all(urlPromises);
        const urlMap = urls.reduce((acc, { id, url }) => {
          if (url) acc[id] = url;
          return acc;
        }, {} as Record<string, string>);

        setSignedUrls(urlMap);
      } catch (error) {
        console.error('Error loading attachments:', error);
      } finally {
        setIsLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [incident.id]);

  const categoryInfo = getCategoryInfo(currentIncident.category);
  const statusInfo = getStatusInfo(currentIncident.status);
  const CategoryIcon = categoryInfo ? ICON_MAP[categoryInfo.icon as keyof typeof ICON_MAP] : AlertTriangle;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
    // Replace space with " à " to get "JJ/MM/AAAA à HH:MM" format
    return formatted.replace(/(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/, '$1 à $2');
  };

  const handleStatusChange = async (newStatus: Incident['status']) => {
    setIsUpdating(true);
    try {
      const result = await updateIncidentStatus(currentIncident.id, newStatus);

      if (result.success && result.data) {
        setCurrentIncident(prev => ({
          ...prev,
          status: result.data!.status,
          resolved_at: result.data!.resolved_at,
          resolved_by: result.data!.resolved_by,
        }));
        showToast('Statut mis à jour avec succès', 'success');
      } else {
        showToast(result.error || 'Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la mise à jour', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAttachmentClick = (attachment: IncidentAttachment) => {
    const signedUrl = signedUrls[attachment.id];

    if (!signedUrl) {
      showToast('URL du fichier non disponible', 'error');
      return;
    }

    if (isImageFile(attachment.file_type)) {
      setLightboxImage(signedUrl);
    } else if (isPdfFile(attachment.file_type)) {
      window.open(signedUrl, '_blank');
    }
  };

  return (
    <>
      <BottomSheet
        isOpen={true}
        onClose={onClose}
        title="Détail de l'incident"
        maxHeight="90vh"
        enableSwipe={true}
      >
        <div className="px-4 py-4 space-y-4">
          {/* Description - Most important element */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-2">Description</div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-900 leading-relaxed">
              {currentIncident.description}
            </div>
          </div>

          {/* Attachments */}
          {isLoadingAttachments ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              <span>Chargement des pièces jointes...</span>
            </div>
          ) : attachments.length > 0 ? (
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                <Paperclip size={14} />
                <span>Pièces jointes ({attachments.length})</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {attachments.map((attachment) => {
                  const isImage = isImageFile(attachment.file_type);
                  const isPdf = isPdfFile(attachment.file_type);
                  const signedUrl = signedUrls[attachment.id];

                  return (
                    <button
                      key={attachment.id}
                      type="button"
                      onClick={() => handleAttachmentClick(attachment)}
                      className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 hover:border-primary-400 transition-all duration-200 active:scale-95"
                    >
                      {isImage ? (
                        signedUrl ? (
                          <img
                            src={signedUrl}
                            alt={attachment.file_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )
                      ) : isPdf ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                          <FileText size={32} className="text-red-500 mb-1" />
                          <span className="text-xs text-gray-600 line-clamp-2 break-all">
                            {attachment.file_name}
                          </span>
                        </div>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                          <FileText size={32} className="text-gray-400 mb-1" />
                          <span className="text-xs text-gray-600 line-clamp-2 break-all">
                            {attachment.file_name}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Incident Information */}
          <div className="space-y-3 pt-2">
            {/* Created at */}
            <div className="flex items-start gap-2">
              <Calendar size={18} className="text-gray-400 mt-0.5" />
              <div>
                <div className="text-xs text-gray-500">Date de création</div>
                <div className="text-sm text-gray-900 font-medium">{formatDate(currentIncident.created_at)}</div>
              </div>
            </div>

            {/* User */}
            <div className="flex items-start gap-2">
              <User size={18} className="text-gray-400 mt-0.5" />
              <div>
                <div className="text-xs text-gray-500">Signalé par</div>
                <div className="text-sm text-gray-900 font-medium">{getFullName(currentIncident.user)}</div>
              </div>
            </div>

            {/* Site */}
            <div className="flex items-start gap-2">
              <MapPin size={18} className="text-gray-400 mt-0.5" />
              <div>
                <div className="text-xs text-gray-500">Site</div>
                <div className="text-sm text-gray-900 font-medium">{currentIncident.site.name}</div>
              </div>
            </div>

            {/* Category */}
            <div className="flex items-start gap-2">
              <CategoryIcon size={18} className="text-gray-400 mt-0.5" />
              <div>
                <div className="text-xs text-gray-500">Thématique</div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getCategoryColorClass(currentIncident.category)}`}>
                  {categoryInfo?.label}
                </span>
              </div>
            </div>

            {/* Resolved info */}
            {(currentIncident.status === 'resolved' || currentIncident.status === 'closed') && currentIncident.resolved_at && (
              <div className="flex items-start gap-2">
                <CheckCircle size={18} className="text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500">Résolu le</div>
                  <div className="text-sm text-gray-900 font-medium">{formatDate(currentIncident.resolved_at)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Status Management - Dropdown for authorized roles */}
          {!isReadOnly && (
            <div className="pt-2">
              <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-2">
                Statut de l'incident
              </label>
              <CustomSelect
                value={currentIncident.status}
                onChange={(value) => handleStatusChange(value as Incident['status'])}
                options={INCIDENT_STATUSES.map(status => ({
                  value: status.value,
                  label: status.label
                }))}
                disabled={isUpdating}
                renderOption={(option) => {
                  const statusColor = getStatusColorClass(option.value);
                  return (
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusColor.includes('green') ? 'bg-green-500' : statusColor.includes('yellow') ? 'bg-yellow-500' : statusColor.includes('blue') ? 'bg-blue-500' : 'bg-gray-500'}`} />
                      <span>{option.label}</span>
                    </div>
                  );
                }}
                renderValue={(option) => {
                  const statusColor = getStatusColorClass(option.value);
                  return (
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusColor.includes('green') ? 'bg-green-500' : statusColor.includes('yellow') ? 'bg-yellow-500' : statusColor.includes('blue') ? 'bg-blue-500' : 'bg-gray-500'}`} />
                      <span>{option.label}</span>
                    </div>
                  );
                }}
              />
              {isUpdating && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <span>Mise à jour en cours...</span>
                </div>
              )}
            </div>
          )}

          {/* Read-only status display for directors and operators */}
          {isReadOnly && (
            <div className="pt-2">
              <div className="text-sm font-semibold text-gray-700 mb-2">Statut actuel</div>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border ${getStatusColorClass(currentIncident.status)}`}>
                {statusInfo?.label}
              </span>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Lightbox for image preview */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 text-white"
            aria-label="Fermer"
          >
            <XIcon size={24} />
          </button>
          <img
            src={lightboxImage}
            alt="Aperçu"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
